import PropTypes from 'prop-types';
import React from 'react';

import logger from '@/shared/utils/logger';

import EmbeddedMapFrame from './EmbeddedMapFrame';

/**
 * Field on the Payload Contract's `map` object naming the render category.
 *
 * Note this is NOT `render_category`. That is Ticket 2's database column;
 * Ticket 9's node reads the column and writes this differently-named wire
 * field. If maps silently stop rendering, verify this name first — a mismatch
 * here produces no error, just a permanent no-op.
 */
const DISCRIMINATOR = 'render_type';

/**
 * Value for third-party iframe embeds. One of Ticket 2's render_category
 * CHECK values: choropleth, embed, points, proportional, lines, routes,
 * not_mappable.
 */
const EMBED_RENDER_TYPE = 'embed';

/**
 * Renderer registry, and the snake_case -> camelCase boundary. The Payload
 * Contract speaks `embed_code`/`fallback_url`; the components take
 * `embedCode`/`fallbackUrl`. Translating here keeps EmbeddedMapFrame
 * independent of contract naming.
 *
 * Ticket 10 adds choropleth and points by adding entries here — one place,
 * rather than editing both ChatMessages and BiliChat again.
 *
 * `toProps` deliberately returns a plain object rather than JSX: an arrow
 * function returning JSX is classified as an anonymous component by
 * eslint-plugin-react, which would then demand propTypes for it.
 */
const RENDERERS = {
    [EMBED_RENDER_TYPE]: {
        Component: EmbeddedMapFrame,
        toProps: (payload) => ({
            embedCode: payload.embed_code,
            fallbackUrl: payload.fallback_url,
            title: payload.source?.name,
            embeddable: payload.embeddable,
        }),
    },
};

/**
 * Dispatch a Payload Contract `map` object to the renderer for its render type.
 *
 * Renders nothing for an absent payload or an unrecognised render type, so a
 * choropleth payload arriving before Ticket 10 lands degrades to no map rather
 * than a crash.
 */
export default function MapPayload({ payload }) {
    const renderType = payload?.[DISCRIMINATOR];
    const entry = RENDERERS[renderType];

    React.useEffect(() => {
        // A payload with no renderer is either the expected transitional state
        // before Ticket 10 lands, or a contract mismatch on DISCRIMINATOR.
        // Logging the received value distinguishes them: `undefined` means the
        // field name is wrong, a real value means the type is unimplemented.
        if (payload && !RENDERERS[renderType]) {
            logger.warn(
                { render_type: renderType },
                'Map payload with no matching renderer'
            );
        }
    }, [payload, renderType]);

    if (!entry) return null;

    const { Component, toProps } = entry;
    return <Component {...toProps(payload)} />;
}

MapPayload.propTypes = {
    /** The `map` object from the Payload Contract; absent when no map applies */
    payload: PropTypes.shape({
        render_type: PropTypes.string,
        embed_code: PropTypes.string,
        fallback_url: PropTypes.string,
        embeddable: PropTypes.bool,
        source: PropTypes.shape({
            name: PropTypes.string,
        }),
    }),
};
